import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

import { i18n } from "../../translate/i18n";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
// import api from "../../services/api";
import axios from "axios";
import usePlans from "../../hooks/usePlans";
import useSettings from "../../hooks/useSettings";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    paddingBottom: 100
  },
  mainHeader: {
    marginTop: theme.spacing(1),
  },
  elementMargin: {
    padding: theme.spacing(2),
  },
  formContainer: {
    maxWidth: 500,
  },
  textRight: {
    textAlign: "right"
  }
}));

const MessagesAPI = () => {
  const classes = useStyles();
  const history = useHistory();

  const [formMessageTextData,] = useState({ token: '', number: '', body: '' })
  const [formMessageMediaData,] = useState({ token: '', number: '', medias: '' })
  const [file, setFile] = useState({})

  const { getPlanCompany } = usePlans();
  const { getAll: getSettings, update: updateSetting } = useSettings();

  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [loadingWebhookSettings, setLoadingWebhookSettings] = useState(true);
  const [savingWebhookUrl, setSavingWebhookUrl] = useState(false);
  const [updatingWebhookEnabled, setUpdatingWebhookEnabled] = useState(false);
  const [suppressWebhookOnAccepted, setSuppressWebhookOnAccepted] = useState(false);
  const [updatingSuppressWebhookOnAccepted, setUpdatingSuppressWebhookOnAccepted] = useState(false);
  const [apiToken, setApiToken] = useState("");
  const [updatingApiToken, setUpdatingApiToken] = useState(false);
  const [copyingApiToken, setCopyingApiToken] = useState(false);

  const loadWebhookSettings = useCallback(async () => {
    setLoadingWebhookSettings(true);
    try {
      const settings = await getSettings();
      const enabledSetting = settings.find(
        setting => setting.key === "messageWebhook.enabled"
      );
      const urlSetting = settings.find(
        setting => setting.key === "messageWebhook.url"
      );
      const tokenSetting = settings.find(
        setting => setting.key === "externalApiToken"
      );
      const skipOnAcceptedSetting = settings.find(
        setting => setting.key === "messageWebhook.skipWhenAccepted"
      );
      setWebhookEnabled(enabledSetting?.value === "enabled");
      setWebhookUrl(urlSetting?.value || "");
      setApiToken(tokenSetting?.value || "");
      setSuppressWebhookOnAccepted(skipOnAcceptedSetting?.value === "enabled");
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingWebhookSettings(false);
    }
  }, [getSettings]);

  const handleToggleWebhook = async (event) => {
    const newValue = event.target.checked;
    setWebhookEnabled(newValue);
    setUpdatingWebhookEnabled(true);
    try {
      await updateSetting({
        key: "messageWebhook.enabled",
        value: newValue ? "enabled" : "disabled"
      });
      toast.success("Webhook atualizado com sucesso.");
    } catch (err) {
      setWebhookEnabled(!newValue);
      toastError(err);
    } finally {
      setUpdatingWebhookEnabled(false);
    }
  };

  const handleWebhookUrlChange = (event) => {
    setWebhookUrl(event.target.value);
  };

  const handleToggleSuppressWebhookOnAccepted = async (event) => {
    const newValue = event.target.checked;
    setSuppressWebhookOnAccepted(newValue);
    setUpdatingSuppressWebhookOnAccepted(true);
    try {
      await updateSetting({
        key: "messageWebhook.skipWhenAccepted",
        value: newValue ? "enabled" : "disabled"
      });
      toast.success("Preferência de webhook atualizada.");
    } catch (err) {
      setSuppressWebhookOnAccepted(!newValue);
      toastError(err);
    } finally {
      setUpdatingSuppressWebhookOnAccepted(false);
    }
  };

  const handleSaveWebhookUrl = async () => {
    setSavingWebhookUrl(true);
    try {
      await updateSetting({
        key: "messageWebhook.url",
        value: webhookUrl.trim()
      });
      toast.success("URL do webhook atualizada.");
    } catch (err) {
      toastError(err);
    } finally {
      setSavingWebhookUrl(false);
    }
  };

  const generateRandomToken = () => {
    if (window.crypto && window.crypto.getRandomValues) {
      const array = new Uint8Array(32);
      window.crypto.getRandomValues(array);
      return Array.from(array, byte => byte.toString(16).padStart(2, "0")).join("");
    }

    return Array.from({ length: 64 })
      .map(() => Math.floor(Math.random() * 16).toString(16))
      .join("");
  };

  const handleGenerateApiToken = async () => {
    const newToken = generateRandomToken();
    setUpdatingApiToken(true);
    try {
      await updateSetting({
        key: "externalApiToken",
        value: newToken
      });
      setApiToken(newToken);
      toast.success("Token de API atualizado.");
    } catch (err) {
      toastError(err);
    } finally {
      setUpdatingApiToken(false);
    }
  };

  const handleCopyApiToken = async () => {
    if (!apiToken) {
      return;
    }
    try {
      setCopyingApiToken(true);
      await navigator.clipboard.writeText(apiToken);
      toast.success("Token copiado para a área de transferência.");
    } catch (err) {
      toastError(err);
    } finally {
      setCopyingApiToken(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const companyId = localStorage.getItem("companyId");
        const planConfigs = await getPlanCompany(undefined, companyId);
        if (!planConfigs.plan.useExternalApi) {
          toast.error("messagesAPI.toasts.unauthorized");
          setLoadingWebhookSettings(false);
          setTimeout(() => {
            history.push(`/`);
          }, 1000);
          return;
        }
        await loadWebhookSettings();
      } catch (err) {
        toastError(err);
        setLoadingWebhookSettings(false);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEndpoint = () => {
    return process.env.REACT_APP_BACKEND_URL + '/api/messages/send'
  }

  const handleSendTextMessage = async (values) => {
    const { number, body } = values;
    const data = { number, body };
    try {
      await axios.request({
        url: getEndpoint(),
        method: 'POST',
        data,
        headers: {
          'Content-type': 'application/json',
          'Authorization': `Bearer ${values.token}`
        }
      })
      toast.success(i18n.t('messagesAPI.toasts.success'));
    } catch (err) {
      toastError(err);
    }
  }

  const handleSendMediaMessage = async (values) => {
    try {
      const firstFile = file[0];
      const data = new FormData();
      data.append('number', values.number);
      data.append('body', firstFile.name);
      data.append('medias', firstFile);
      await axios.request({
        url: getEndpoint(),
        method: 'POST',
        data,
        headers: {
          'Content-type': 'multipart/form-data',
          'Authorization': `Bearer ${values.token}`
        }
      })
      toast.success(i18n.t('messagesAPI.toasts.success'));
    } catch (err) {
      toastError(err);
    }
  }

  const renderFormMessageText = () => {
    return (
      <Formik
        initialValues={formMessageTextData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSendTextMessage(values);
            actions.setSubmitting(false);
            actions.resetForm()
          }, 400);
        }}
        className={classes.elementMargin}
      >
        {({ isSubmitting }) => (
          <Form className={classes.formContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.token")}
                  name="token"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.number")}
                  name="number"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.body")}
                  name="body"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12} className={classes.textRight}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  ) : i18n.t('messagesAPI.buttons.send')}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    )
  }

  const renderFormMessageMedia = () => {
    return (
      <Formik
        initialValues={formMessageMediaData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSendMediaMessage(values);
            actions.setSubmitting(false);
            actions.resetForm()
            document.getElementById('medias').files = null
            document.getElementById('medias').value = null
          }, 400);
        }}
        className={classes.elementMargin}
      >
        {({ isSubmitting }) => (
          <Form className={classes.formContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.mediaMessage.token")}
                  name="token"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.mediaMessage.number")}
                  name="number"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <input type="file" name="medias" id="medias" required onChange={(e) => setFile(e.target.files)} />
              </Grid>
              <Grid item xs={12} className={classes.textRight}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  ) : i18n.t('messagesAPI.buttons.send')}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    )
  }

  return (
    <Paper
      className={classes.mainPaper}
      style={{marginLeft: "5px"}}
      // className={classes.elementMargin}
      variant="outlined"
    >

      <Divider className={classes.elementMargin} />
      <Typography variant="h5">
        {i18n.t('messagesAPI.labels.doc')}
      </Typography>
      <Typography variant="h6" color="primary" className={classes.elementMargin}>
        {i18n.t('messagesAPI.labels.method')}
      </Typography>
      <Typography component="div">
        <ol>
          <li>
            {i18n.t('messagesAPI.labels.textMessage')}
          </li>
          <li>
            {i18n.t('messagesAPI.labels.mediaMessage')}
          </li>
        </ol>
      </Typography>
      <Typography variant="h6" color="primary" className={classes.elementMargin}>
        {i18n.t('messagesAPI.labels.instructions')}
      </Typography>
      <Typography className={classes.elementMargin} component="div">
        <b>{i18n.t('messagesAPI.labels.observations')}</b><br />
        <ul>
          <li>{i18n.t('messagesAPI.labels.before1')} <br />{i18n.t('messagesAPI.labels.before2')}</li>
          <li>
            {i18n.t('messagesAPI.labels.numberDescription')}
            <ul>
              <li>{i18n.t('messagesAPI.labels.countryCode')}</li>
              <li>DDD</li>
              <li>{i18n.t('messagesAPI.labels.number')}</li>
            </ul>
          </li>
        </ul>
      </Typography>
      <Typography variant="h6" color="primary" className={classes.elementMargin}>
        {i18n.t('messagesAPI.labels.textMessage2')}
      </Typography>
      <Grid container>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.elementMargin} component="div">
            <p>{i18n.t('messagesAPI.labels.textMessageInstructions')}</p>
            <b>Endpoint: </b> {getEndpoint()} <br />
            <b>{i18n.t('messagesAPI.labels.method2')}: </b> POST <br />
            <b>Headers: </b> Authorization (Bearer token) {i18n.t('messagesAPI.labels.e')} Content-Type (application/json) <br />
            <b>Body: </b> {"{ \"number\": \"5599999999999\", \"body\": \"Sua mensagem\" }"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.elementMargin}>
            <b>{i18n.t('messagesAPI.labels.tests')}</b>
          </Typography>
          {renderFormMessageText()}
        </Grid>
      </Grid>
      <Typography variant="h6" color="primary" className={classes.elementMargin}>
        {i18n.t('messagesAPI.labels.mediaMessage2')}
      </Typography>
      <Grid container>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.elementMargin} component="div">
            <p>{i18n.t('messagesAPI.labels.textMessageInstructions')}</p>
            <b>Endpoint: </b> {getEndpoint()} <br />
            <b>{i18n.t('messagesAPI.labels.method2')}: </b> POST <br />
            <b>Headers: </b> Authorization (Bearer token) {i18n.t('messagesAPI.labels.e')} Content-Type (multipart/form-data) <br />
            <b>FormData: </b> <br />
            <ul>
              <li>
                <b>number: </b> 5599999999999
              </li>
              <li>
                <b>medias: </b> arquivo
              </li>
            </ul>
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.elementMargin}>
            <b>{i18n.t('messagesAPI.labels.tests')}</b>
          </Typography>
          {renderFormMessageMedia()}
        </Grid>
      </Grid>
      <Divider className={classes.elementMargin} />
      <Box className={classes.elementMargin}>
        <Typography variant="h6" color="primary">
          Webhook de Mensagens Recebidas
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Determine a URL que receberá os dados sempre que uma nova mensagem chegar.
        </Typography>
        {loadingWebhookSettings ? (
          <Box mt={2} display="flex" alignItems="center">
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={webhookEnabled}
                      onChange={handleToggleWebhook}
                      color="primary"
                      disabled={updatingWebhookEnabled}
                    />
                  }
                  label="Ativar webhook de mensagens recebidas"
                />
                {updatingWebhookEnabled && (
                  <CircularProgress size={18} style={{ marginLeft: 8 }} />
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={suppressWebhookOnAccepted}
                      onChange={handleToggleSuppressWebhookOnAccepted}
                      color="primary"
                      disabled={updatingSuppressWebhookOnAccepted}
                    />
                  }
                  label="Não enviar webhook quando o ticket estiver em atendimento"
                />
                {updatingSuppressWebhookOnAccepted && (
                  <CircularProgress size={18} style={{ marginLeft: 8 }} />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label="URL do Webhook"
                value={webhookUrl}
                onChange={handleWebhookUrlChange}
                fullWidth
                variant="outlined"
                margin="dense"
                disabled={savingWebhookUrl || updatingWebhookEnabled}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveWebhookUrl}
                disabled={savingWebhookUrl || updatingWebhookEnabled}
                fullWidth
              >
                {savingWebhookUrl ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Salvar URL"
                )}
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
      <Box className={classes.elementMargin}>
        <Typography variant="h6" color="primary">
          Token de API
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Gere ou regenere um token Bearer para consumir os endpoints de Tickets.
        </Typography>
        <Grid container spacing={2} alignItems="center" style={{ marginTop: 8 }}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Token"
              value={apiToken}
              fullWidth
              variant="outlined"
              margin="dense"
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={1}>
              <Grid item xs={apiToken ? 6 : 12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateApiToken}
                  disabled={updatingApiToken}
                  fullWidth
                >
                  {updatingApiToken ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : apiToken ? (
                    "Regenerar"
                  ) : (
                    "Gerar"
                  )}
                </Button>
              </Grid>
              {apiToken && (
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleCopyApiToken}
                    disabled={copyingApiToken}
                    fullWidth
                  >
                    {copyingApiToken ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Copiar"
                    )}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Divider className={classes.elementMargin} />
      <Typography variant="h6" color="primary" className={classes.elementMargin}>
        Endpoints disponíveis
      </Typography>
      <Typography className={classes.elementMargin} component="div">
        <ul>
          <li>
            <b>POST</b> <code>/api/tickets/messages</code> — envia mensagens ou mídias para um ticket existente (enviar <code>ticketId</code> no corpo, além de <code>body</code> e/ou <code>medias</code> e opcional <code>quotedMsgId</code>).
          </li>
          <li>
            <b>POST</b> <code>/api/tickets/transfer</code> — altera a fila (<code>queueId</code>) e/ou o atendente (<code>userId</code>) informando o <code>ticketId</code> no corpo.
          </li>
          <li>
            <b>POST</b> <code>/api/tickets/close</code> — encerra o ticket (corpo com <code>ticketId</code>).
          </li>
        </ul>
        Sempre envie o cabeçalho <code>Authorization: Bearer {'{token}'}</code> com o token acima.
      </Typography>
    </Paper>
  );
};

export default MessagesAPI;
